import { useGetDownloadUrl } from '@/shared/hooks/useGetDownloadUrl';
import type { AudienceFlowWidgetData } from '../model/audience-flow-widget.types';
import { useTranslation } from 'react-i18next';
import { i18n } from '@/shared/i18n';
import { isDistinctTitle } from '@/shared/lib/persona-label';

type Persona = AudienceFlowWidgetData['personas'][number];

interface Props {
  data?: AudienceFlowWidgetData;
}

const personaName = (persona: Persona): string =>
  persona.personaTitle ||
  persona.segmentName ||
  persona.demographicsData?.data.firstName ||
  i18n.t('audiences:audienceFlowWidget.audienceFlowThumbnail.persona');

const personaSubtitle = (persona: Persona): string | null =>
  isDistinctTitle(persona) ? (persona.segmentName ?? null) : null;

/**
 * Circular avatar resolved live from the persona's photoAssetId. Falls back to
 * the name's initial while the download-url query is in flight or when the
 * persona carries no photo, so the card never shows a broken image.
 */
function PersonaAvatar(props: { assetId?: string; name: string }) {
  const { downloadUrl } = useGetDownloadUrl(props.assetId);
  if (downloadUrl) {
    return (
      <img
        src={downloadUrl}
        alt={props.name}
        className="size-10 shrink-0 rounded-full object-cover"
      />
    );
  }
  return (
    <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#C9DDFB] text-sm font-semibold text-[#1A3A6B]">
      {props.name.charAt(0).toUpperCase()}
    </span>
  );
}

/**
 * Prop-only preview of an audience-flow widget: the primary persona rendered as
 * a card — avatar, name, and segment — on the persona accent background, echoing
 * the full persona card. Reads props.data only; the avatar fetch is a read-only
 * download-url query (cached per assetId), never the widget store or deps bag.
 */
export const AudienceFlowThumbnail = (props: Props) => {
  const { t } = useTranslation('audiences');

  const personas = Array.isArray(props.data?.personas) ? props.data.personas : [];

  if (personas.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-[#E8F1FE]">
        <p className="text-sm font-semibold text-[#1A1A1A]">
          {t('audienceFlowWidget.audienceFlowThumbnail.audience')}
        </p>
      </div>
    );
  }

  const persona = personas.find((p) => p.id === props.data?.selectedPersonaId) ?? personas[0];

  const name = personaName(persona);
  const subtitle = personaSubtitle(persona);

  return (
    <div className="flex h-full w-full flex-col gap-3 overflow-hidden bg-[#E8F1FE]">
      <PersonaAvatar assetId={persona.photoAssetId} name={name} />
      <div className="flex flex-col gap-1">
        <p className="line-clamp-2 text-sm font-semibold text-[#1A1A1A]">{name}</p>
        {subtitle && <p className="line-clamp-1 text-xs text-[#6C6962]">{subtitle}</p>}
      </div>
    </div>
  );
};
